import {FACTIONS} from '../../constants/factions';
import {UNIT_TYPES} from '../units';
import {PHASES, WAR_PHASE_STAGES} from '../../constants/phases';
import {COMMON_BUILDING_TOKENS} from '../../constants/tokens';
import {GROUND_BACKGROUNDS} from '../groundBackgrounds';
import {buildTutorialBarbaSteps} from './tutorialBarbaReplicas';

/**
 * Глава 1: базовый цикл игры — стройка, найм, снабжение, бой, захват города.
 *
 * === Формат главы ===
 * - fieldPath: поле матча (глава 1 — с нуля; главы 2–3 — снапшоты состояния).
 * - groundBackground: подложка поля (configs/groundBackgrounds) — у главы она
 *   всегда одна и та же, случайной не разыгрывается.
 * - playerFaction/botFaction: фракции; игрок всегда синий, бот красный (цвета
 *   снапшотных глав заданы в самом снапшоте — у Вентори в главе 3 оранжевый).
 * - ui: флаги скрытия интерфейса (рынок политик, перехват инициативы).
 * - botActionIntervalMs: свой темп действий бота в главе (по умолчанию общий
 *   TUTORIAL_BOT_ACTION_INTERVAL_MS).
 * - steps: последовательность шагов. Шаг:
 *     id            — идентификатор (логи, ключ метрик шага, ожидания
 *                     сценария бота);
 *     modalId       — обучающая модалка при входе в шаг (реестр:
 *                     src/components/Tutorial/tutorialModalsRegistry.js);
 *                     у каждого шага свой — по нему считается номер шага
 *                     модалки в метриках;
 *     speaker       — чья реплика в модалке шага (TUTORIAL_SPEAKERS; шаги
 *                     реплик Барбы в начале главы — buildTutorialBarbaSteps
 *                     из tutorialBarbaReplicas.js);
 *     modalArrowUiSelector — CSS-селектор элемента, на который стрелка
 *                     указывает ПОВЕРХ открытой модалки шага (модалка про
 *                     кнопку — стрелка на кнопку);
 *     task          — {text, hint?, arrowHexKey?, arrowUiSelector?,
 *                     arrowHexKeyStage?} — текущая задача в панели задач;
 *                     стрелка на ячейку или на элемент интерфейса
 *                     (CSS-селектор); arrowHexKeyStage — для шагов «заверши
 *                     этап и сделай ход»: стрелка на ячейку только на этом
 *                     этапе войны, до него — DOM-стрелка arrowUiSelector;
 *                     arrowUiSelectorCandidates — явная цепочка CSS-селекторов
 *                     DOM-стрелки (первый видимый побеждает);
 *                     arrowAboveModals — DOM-стрелка поверх игровых модалок
 *                     (цели внутри открытых правил);
 *                     duringOpponentTurn — задание выполняется в ход бота
 *                     (иначе в ход бота панель пишет «Дождись окончания хода
 *                     противника»);
 *     allow         — действия, разрешенные шагом (см. tutorialActionGate);
 *                     остальные блокируются с info-тостом задачи;
 *     complete      — предикат выполнения по состоянию (см.
 *                     tutorialStepPredicates); шаг без complete — чисто
 *                     модальный: продвигается закрытием модалки. Шаг с
 *                     check: 'rulesSectionScrolled' — особый: подсказку в нем
 *                     рисует и прокрутку засчитывает TutorialRulesScrollHint
 *                     внутри открытых правил, поэтому ни modalId, ни стрелок
 *                     у такого шага быть не должно (под оверлеем правил
 *                     модалка не показывается, а стрелка уводится на кнопку
 *                     «Скрыть»);
 *     dice          — {damage: [...]} — фиксированные кости, встающие в
 *                     очередь при входе в шаг (грани -3..1; порядок
 *                     потребления: по парам за отряд в порядке расчета боя);
 *     autoCompleteTurnAfterModal — обучение само завершает ход игрока, когда
 *                     тот закроет модалку шага (пустая фаза Снабжения первого
 *                     раунда: кормить некого — см. tutorialMiddleware);
 *     deselectsUnitOnEntry — сброс выбранного отряда на входе в шаг (плюс
 *                     подавление автовыбора при найме, см.
 *                     recruitNewUnitOnBarracks) — для шагов, где игрок должен
 *                     выбрать отряд руками (докупка размера после найма).
 * - botScript: детерминированный сценарий бота; ключ "раунд|фаза|этап" ->
 *   массив операций (см. tutorialBotScript). Исчерпание операций = бот
 *   завершает фазу/этап.
 */
export const TUTORIAL_CHAPTER_1 = {
  id: 'tutorial_chapter_1',
  title: 'Обучение: Глава 1',
  shortTitle: 'Глава 1',
  description: 'Основы: строительство, найм отрядов, снабжение, бой и захват города',
  fieldPath: '/fields/tutorial.json',
  groundBackground: GROUND_BACKGROUNDS.GREEN_2,
  playerFaction: FACTIONS.HUMANS,
  botFaction: FACTIONS.ORCS,
  botName: 'Вождь Орков',
  // Секунда между действиями орка вместо общих для обучения трех: ход бота в
  // первой главе короткий и предсказуемый, и стандартная пауза превращается в
  // ожидание там, где смотреть особо не на что
  botActionIntervalMs: 1000,
  // Финальный раунд фиксируется конфигом главы: сценарий рассчитан на свои
  // раунды и не должен зависеть от общего FINAL_ROUND (его меняют для отладки)
  finalRound: 13,
  // Надбавка к обычным стартовым монетам — ровно цена второй фермы (шаг
  // build_farm_2): остальной бюджет главы рассчитан на обычный старт, и без
  // надбавки отряд не дорос бы к бою до размера 6
  playerExtraStartingCoins: 3,
  // Награда за ПЕРВОЕ прохождение главы (начисляет сервер в
  // /users/me/tutorial-chapters/complete, см. server/index.js)
  reward: {type: 'premium', days: 3},
  ui: {
    hidePolicyMarket: true,
    hideInitiativeInterception: true,
  },

  steps: [
    // Реплики Барбы — цепочка окон до приветствия главы
    ...buildTutorialBarbaSteps('ch1'),
    {
      id: 'welcome',
      modalId: 'ch1_welcome',
    },
    {
      id: 'place_starting_quarter',
      modalId: 'ch1_setup_quarter',
      task: {
        text: 'Размести стартовый Центральный квартал на указанной ячейке с флагом',
        arrowHexKey: '0,3',
      },
      allow: [{action: 'placeStartingQuarter', hexKey: '0,3'}],
      complete: {check: 'startingQuarterAt', hexKey: '0,3', color: 'blue'},
    },
    {
      id: 'confirm_setup',
      task: {
        text: 'Подтверди выбор кнопкой завершения хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 1, currentPlayerColor: 'blue'},
    },
    {
      // Первая фаза Снабжения пустая: отрядов еще нет, кормить некого.
      // Обучение завершает ее само, как только игрок закроет модалку про фазы
      // раунда (autoCompleteTurnAfterModal — см. tutorialMiddleware); allow
      // остается запасным ручным путем и нужен headless-пробнику главы
      id: 'skip_empty_supply',
      modalId: 'ch1_round_phases',
      autoCompleteTurnAfterModal: true,
      task: {
        text: 'Отрядов пока нет — фаза Снабжения пропускается сама',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, currentPlayerColor: 'blue'},
    },
    {
      id: 'build_quarter_mine',
      modalId: 'ch1_construction',
      task: {
        text: 'Построй Базовый квартал на указанной ячейке — рудник рядом присоединится к городу',
        hint: 'Выбери квартал в панели снизу и нажми на указанную ячейку',
        arrowHexKey: '1,3',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '1,3'}],
      complete: {check: 'hexToken', hexKey: '1,3', token: COMMON_BUILDING_TOKENS.QUARTER},
    },
    {
      // Доход: базовые +2 за город и +1 от присоединенного рудника — новое
      // значение появится после завершения фазы (см. Ch1BaseIncomeModal)
      id: 'base_income_explained',
      modalId: 'ch1_base_income',
    },
    {
      // Знакомство с кнопкой отмены: до этого шага она скрыта вовсе
      // (revealsUndoButton — см. checkIfTutorialUndoButtonRevealed).
      // Единственный шаг обучения, где отмена РАБОТАЕТ (allow: 'undo' +
      // checkIfTutorialUndoAllowed): игрок откатывает только что построенный
      // квартал. Стрелка на кнопку — после закрытия модалки (на мобильном
      // модалка перекрывает футер, стрелка поверх нее была бы в пустоту)
      id: 'undo_button_try',
      modalId: 'ch1_undo_button',
      revealsUndoButton: true,
      task: {
        text: 'Отмени постройку квартала кнопкой отмены действия',
        arrowUiSelector: '[data-tutorial-select="undo_button"]',
      },
      allow: [{action: 'undo'}],
      complete: {check: 'hexTokenGone', hexKey: '1,3'},
    },
    {
      id: 'rebuild_quarter',
      task: {
        text: 'Теперь построй Базовый квартал на той же ячейке снова',
        arrowHexKey: '1,3',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '1,3'}],
      complete: {check: 'hexToken', hexKey: '1,3', token: COMMON_BUILDING_TOKENS.QUARTER},
    },
    {
      // Отмена знакома — дальше по сценарию она снова отключена (кнопка
      // отвечает подсказкой), чтобы игрок не сбился с выверенных шагов
      id: 'undo_all_phases',
      modalId: 'ch1_undo_phases',
    },
    {
      id: 'build_barracks',
      task: {
        text: 'Построй Казарму — в ней нанимаются отряды',
        arrowHexKey: '0,2',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.BARRACKS, hexKey: '0,2'}],
      complete: {check: 'hexToken', hexKey: '0,2', token: COMMON_BUILDING_TOKENS.BARRACKS},
    },
    {
      id: 'build_farm',
      task: {
        text: 'Построй Ферму — она будет снабжать твои отряды',
        arrowHexKey: '-1,3',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.FARM, hexKey: '-1,3'}],
      complete: {check: 'hexToken', hexKey: '-1,3', token: COMMON_BUILDING_TOKENS.FARM},
    },
    {
      // Вторая ферма — ради двух пайков отряду перед боем в Р2 (второй паёк —
      // дополнительное снабжение, за него отряд перебросит кости). Строится
      // сейчас: в Р2 снабжение идет раньше строительства. 2,3 — у квартала и
      // рудника, в стороне от пути отряда к нейтралу
      id: 'build_farm_2',
      task: {
        text: 'Построй вторую Ферму — в следующем раунде отряд получит сразу два пайка',
        arrowHexKey: '2,3',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.FARM, hexKey: '2,3'}],
      complete: {check: 'hexToken', hexKey: '2,3', token: COMMON_BUILDING_TOKENS.FARM},
    },
    {
      id: 'complete_construction_r1',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        currentPlayerColor: 'blue',
      },
    },
    {
      // Фаза Войны началась: что это за фаза и ее 5 этапов, затем (цепочкой
      // модалок) — как завершать этапы кнопкой завершения хода
      id: 'war_phase_intro',
      modalId: 'ch1_war_phase',
    },
    {
      id: 'war_phase_complete_button',
      modalId: 'ch1_war_phase_button',
    },
    {
      id: 'recruit_d6',
      modalId: 'ch1_unit_types',
      task: {
        text: 'Найми обычную Пехоту: выбери её в панели снизу и нажми на казарму',
        arrowHexKey: '0,2',
      },
      allow: [{action: 'recruitUnit', unitType: UNIT_TYPES.D6.id, hexKey: '0,2'}],
      complete: {check: 'unitRecruitedAt', hexKey: '0,2', type: UNIT_TYPES.D6.id},
    },
    {
      id: 'size_recruitment_r1',
      modalId: 'ch1_size_recruitment',
      task: {
        text: 'Докупи отряду размер дважды: нажми на отряд, затем на подсвеченные кварталы',
        hint: 'Каждый квартал города даёт один найм размера за фазу Войны',
        arrowHexKey: '0,2',
      },
      // Нанятый на прошлом шаге отряд автовыбирается — сбрасываем, чтобы
      // игрок сам кликнул отряд и увидел подсветку кварталов в ответ
      deselectsUnitOnEntry: true,
      allow: [{action: 'sizeRecruitment'}],
      complete: {check: 'unitSizeRecruitedAt', hexKey: '0,2', count: 2},
    },
    {
      id: 'complete_war_r1',
      task: {
        text: 'Заверши этап найма, затем этап перемещения — в этом раунде никуда не идём',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 2, currentPlayerColor: 'blue'},
    },
    {
      id: 'supply_r2',
      modalId: 'ch1_supply',
      task: {
        text: 'Снабди отряд: нажми на ферму, затем на отряд',
        hint: 'Отряд без снабжения теряет 2 размера в конце фазы!',
        arrowHexKey: '-1,3',
      },
      // Ферм две — первый паёк можно отдать с любой (иначе клик по второй
      // ферме блокировался бы тостом про уже отданный паёк)
      allow: [{action: 'supply', unitHexKey: '0,2'}],
      complete: {check: 'unitSuppliedAt', hexKey: '0,2'},
    },
    {
      // Что такое второй паёк и зачем он отряду перед боем. Отдельный
      // модальный шаг: пока модалка не закрыта, второй паёк не раздать, и
      // объяснение не проскочит
      id: 'additional_supply_explained',
      modalId: 'ch1_additional_supply',
      // Быстрее обычной секунды: игрок только что узнал про «два пайка» и
      // сразу тянется ко второй ферме — пока модалки нет, паёк заблокирован
      modalDelayMs: 400,
    },
    {
      id: 'supply_r2_additional',
      task: {
        text: 'Дай отряду второй паёк с другой фермы: нажми на ферму, затем на отряд',
        hint: 'Второй паёк не увеличит размер — он пригодится отряду в бою',
        // Стрелка — на ферму, у которой паёк остался (см. resolveTutorialArrowHexKeys)
        arrowHexKey: '2,3',
      },
      allow: [{action: 'supply', unitHexKey: '0,2', additional: true}],
      complete: {check: 'unitSuppliedAt', hexKey: '0,2', count: 2},
    },
    {
      id: 'complete_supply_r2',
      task: {
        text: 'Заверши фазу Снабжения — отряд на казарме вырастет сразу на 2',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 2, currentPlayerColor: 'blue'},
    },
    {
      id: 'build_quarter_ruins',
      modalId: 'ch1_win_points',
      // Квартал к руинам — на -1,4 (руины на -2,5): единственная смежная
      // центральному кварталу ячейка рядом с руинами. Руины сознательно
      // далеко от первого квартала 1,3 — иначе присоединились бы уже им
      task: {
        text: 'Построй Базовый квартал рядом с руинами — они приносят победные очки каждый раунд',
        arrowHexKey: '-1,4',
      },
      allow: [{action: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '-1,4'}],
      complete: {check: 'hexToken', hexKey: '-1,4', token: COMMON_BUILDING_TOKENS.QUARTER},
    },
    {
      id: 'complete_construction_r2',
      task: {
        text: 'Заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 2,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'size_up_to_max',
      task: {
        text: 'Докупи отряду размер до максимума (6): нажми на отряд, затем на любой подсвеченный квартал',
        arrowHexKey: '0,2',
      },
      allow: [{action: 'sizeRecruitment'}],
      complete: {check: 'unitSizeRecruitedAt', hexKey: '0,2', count: 1},
    },
    {
      id: 'complete_recruitment_r2',
      task: {
        text: 'Заверши этап найма',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.MOVEMENT,
        minRound: 2,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'move_to_neutral',
      modalId: 'ch1_movement',
      task: {
        text: 'Перемести отряд на указанную ячейку — вплотную к нейтральному отряду',
        hint: 'Нажми на свой отряд, затем на подсвеченную ячейку',
        arrowHexKey: '0,1',
      },
      allow: [{action: 'moveUnit', fromHexKey: '0,2', toHexKey: '0,1'}],
      complete: {check: 'unitMovedTo', toHexKey: '0,1', color: 'blue'},
    },
    {
      id: 'enter_combat',
      modalId: 'ch1_melee_rules',
      // Кости боя: порядок потребления выяснен прогоном — первая пара очереди
      // уходит нейтральному защитнику (он стоит в units первым), вторая —
      // нашему отряду. У нейтрала -3 и 0 -> урон 6-3+0=3, у нашего отряда
      // -2 и -2 -> урон 6-2-2=2: наш удар нейтрала не убьет, отряд перебросит
      // кости за дополнительное снабжение (шаг reroll_combat). Броски у своего
      // отряда и у нейтрала РАЗНЫЕ намеренно: с одинаковыми -2 и -2 игрок путал
      // два расчета в футере. Запас живучести нашего отряда — 3 урона из 6
      dice: {damage: [-3, 0, -2, -2]},
      task: {
        text: 'Заверши этап перемещения — начнётся ближний бой',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.COMBAT,
        minRound: 2,
        currentPlayerColor: 'blue',
      },
    },
    {
      // Знакомство с расчетом боя: игрок обязан кликнуть КАЖДЫЙ отряд-участник
      // и посмотреть детальный расчет урона в футере (SelectedUnitMelee).
      // Выбор отряда гейт не проверяет. Завершение хода и переброс на этом
      // шаге закрыты: у отряда уже есть дополнительное снабжение, а кости для
      // переброса встанут в очередь только на шаге reroll_combat — ранний
      // переброс вышел бы случайным, и нейтрал мог бы выжить
      id: 'inspect_combat_units',
      modalId: 'ch1_combat_results',
      task: {
        text: 'Нажми на каждый из двух отрядов боя — изучи детальный расчёт нанесённого урона',
        // Подсказка — и текст тоста при заблокированном действии: инструкция
        hint: 'Сначала нажми на оба отряда боя — расчёт урона появится в панели снизу',
        // Стрелки на еще не просмотренные отряды (см. resolveTutorialArrowHexKeys)
        inspectUnitsHexKeys: ['0,1', '0,0'],
      },
      allow: [],
      complete: {check: 'unitsInspected', hexKeys: ['0,1', '0,0']},
    },
    {
      // Переброс за дополнительное снабжение — через 3 с после изучения
      // обоих отрядов (игрок только что видел оба расчета: свой слабый на 2
      // урона и чужой на 3). Модальный
      // шаг: пока модалка не закрыта, перебросить нельзя
      id: 'reroll_explained',
      modalId: 'ch1_reroll',
      modalDelayMs: 3000,
    },
    {
      // Шаг начинается закрытием модалки — кости переброса встают в очередь
      // после уже брошенных костей боя: обе кости 0 (лучшая грань) -> урон
      // 6+0+0=6 — ровно размер нейтрала, он уничтожен
      id: 'reroll_combat',
      dice: {damage: [0, 0]},
      task: {
        text: 'Нажми на свой отряд в бою, выбери обе кости и перебрось их',
        hint: 'Переброс доступен отряду с дополнительным снабжением',
        meleeUnitHexKey: '0,1',
        arrowUiSelectorCandidates: [
          '[data-tutorial-melee-owner="own"] .melee-toolbar_stats-container .damage-dice:not(.damage-dice-selected)',
          '[data-tutorial-melee-owner="own"] .reroll-button',
        ],
        arrowUiSelectorMultiple: true,
      },
      allow: [{action: 'meleeInteraction', rerollDiceCount: 2}],
      complete: {check: 'meleeRerolledBy', color: 'blue'},
    },
    {
      // Итог переброса и статистика урона ближнего боя — через 3 с после
      // переброса (игрок успевает увидеть новый расчет)
      id: 'melee_damage_explained',
      modalId: 'ch1_melee_damage',
      modalDelayMs: 3000,
    },
    {
      id: 'resolve_combat',
      task: {
        text: 'Заверши этапы до конца хода',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}, {action: 'meleeInteraction'}],
      complete: {check: 'gameMoment', phase: PHASES.SUPPLY, minRound: 3, currentPlayerColor: 'blue'},
    },
    {
      id: 'supply_r3',
      task: {
        text: 'Снабди отряд с фермы — снабжение дотягивается до него через казарму',
        arrowHexKey: '-1,3',
      },
      // С любой из двух ферм; второй паёк (additional) здесь не нужен
      allow: [{action: 'supply', unitHexKey: '0,1'}],
      complete: {check: 'unitSuppliedAt', hexKey: '0,1'},
    },
    {
      id: 'complete_supply_r3',
      task: {
        text: 'Заверши фазу Снабжения',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'gameMoment', phase: PHASES.CONSTRUCTION, minRound: 3, currentPlayerColor: 'blue'},
    },
    {
      id: 'skip_construction_r3',
      task: {
        text: 'Строить сейчас ничего не нужно — заверши фазу Строительства',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {
        check: 'gameMoment',
        phase: PHASES.WAR,
        stage: WAR_PHASE_STAGES.RECRUITMENT,
        minRound: 3,
        currentPlayerColor: 'blue',
      },
    },
    {
      id: 'capture_city',
      modalId: 'ch1_city_capture',
      task: {
        text: 'Заверши этап найма и перемести отряд на центральный квартал нейтрального города',
        arrowHexKey: '0,0',
        arrowUiSelector: '.complete-phase-button',
        arrowHexKeyStage: WAR_PHASE_STAGES.MOVEMENT,
      },
      allow: [{action: 'completeTurn'}, {action: 'moveUnit', fromHexKey: '0,1', toHexKey: '0,0'}],
      complete: {check: 'unitMovedTo', toHexKey: '0,0', color: 'blue'},
    },
    {
      id: 'finish_round_capture',
      task: {
        text: 'Заверши оставшиеся этапы хода — в конце раунда город станет твоим',
        arrowUiSelector: '.complete-phase-button',
      },
      allow: [{action: 'completeTurn'}],
      complete: {check: 'cityOwnedBy', centralHexKey: '0,0', color: 'blue'},
    },
  ],

  botScript: {
    '0|setup|': [
      {op: 'placeStartingQuarter', hexKey: '0,-3'},
    ],
    '1|construction|': [
      {op: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '1,-4'},
      {op: 'build', token: COMMON_BUILDING_TOKENS.BARRACKS, hexKey: '0,-4'},
      {op: 'build', token: COMMON_BUILDING_TOKENS.FARM, hexKey: '-1,-3'},
    ],
    '1|war|recruitment': [
      {op: 'recruit', unitType: UNIT_TYPES.D6.id, hexKey: '0,-4', unitId: 'tutorial_ch1_bot_d6'},
      {op: 'sizeRecruitment', unitHexKey: '0,-4', quarterHexKey: '0,-3'},
      {op: 'sizeRecruitment', unitHexKey: '0,-4', quarterHexKey: '1,-4'},
    ],
    '2|supply|': [
      {op: 'autoSupply'},
    ],
    '2|construction|': [
      {op: 'build', token: COMMON_BUILDING_TOKENS.QUARTER, hexKey: '1,-3'},
    ],
    '3|supply|': [
      {op: 'autoSupply'},
    ],
  },
};
